// Copyright ©️ 2026 eVolve MEP, LLC

import type { YBCString } from '@evolvemep/api-ts';

import { isNil } from 'helpers/isNotNil';
import {
  BendPreviewGeometry,
  type BendPreviewSpecifications,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import { convertToRadians, getTangentDistance } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';

type ParsedYbcLine = {
  y: number;
  b: number;
  c: number;
  r?: number;
};

type ParsedYbcData = {
  stockLength: number;
  conduitSize: number;
  ybcData: ParsedYbcLine[];
};

export const parseYbc = (ybcString: YBCString): ParsedYbcData => {
  const lines = ybcString
    .trim()
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const machineDataLine = lines.slice(-1)[0];
  if (isNil(machineDataLine)) throw new Error('No machine data line found in YBC string');
  const [_fittingOutsideDiameter, stockLength, conduitSize] = machineDataLine.split('\t').map(Number);

  if (isNil(stockLength) || isNil(conduitSize)) throw new Error('Invalid machine data line in YBC string');

  const ybcLines = lines.slice(1, -1);
  const ybcData = ybcLines.map<ParsedYbcLine>((line) => {
    const [y, b, c, r] = line.split('\t').map(Number);
    if (isNil(y) || isNil(b) || isNil(c)) throw new Error('Invalid YBC string');
    if (Math.abs(c) > 90) throw new Error('Bend angle must be less than or equal to 90 degrees');
    return { y, b, c, r };
  });

  return {
    stockLength,
    conduitSize,
    ybcData,
  };
};

export class YBCGeometry extends BendPreviewGeometry {
  createBendDefs(dimensions: BendPreviewSpecifications) {
    const { ybcString } = dimensions;
    if (isNil(ybcString)) throw new Error('YBC string is required for YBC geometry');

    const { ybcData } = parseYbc(ybcString);
    let tangentDistanceCarry = 0;

    // Simple counter to track the number of bends for labeling purposes. Incremented when a bend is encountered.
    let bendCount = 0;
    return ybcData.map(({ y, b, c, r }, i) => {
      const tangentDistance = c === 0 ? 0 : getTangentDistance(r ?? 0, convertToRadians(c));
      const runLength = (y + tangentDistance + tangentDistanceCarry) / 12;
      tangentDistanceCarry = tangentDistance;
      if (c !== 0) {
        bendCount++;
      }

      return {
        runLength,
        thenRotateBy: convertToRadians(b),
        thenBendBy: convertToRadians(c),
        startLabels: [{ label: `Length ${i + 1}`, length: runLength }],
        endLabels: c === 0 ? [] : [{ label: `Bend ${bendCount}`, angle: c }],
      };
    });
  }
}
